import { useEffect } from "react";
import { Route, Routes, Navigate, useSearchParams } from "react-router-dom";
import { ComposeProviders, FREQUENCIES, modules, cents } from "byzantine";
import { type LimitsForDisplayType } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { initialValues } from "byzantine/src/dbbl/businessLogic/modules/transfers/TransferFunds.form";
import Container from "../../Container";
import TransfersContainer from "./TransfersContainer";
import TransferDetailsComponent from "./TransferDetails";
import TransferReview from "./TransferReview";
import TransferReceipt from "./TransferReceipt";

export const TRANSFERS_ROUTE = "funds";
export const TRANSFER_DETAILS_ROUTE = "details";
export const TRANSFER_REVIEW_ROUTE = "review";
export const TRANSFER_RECEIPT_ROUTE = "receipt";

const RequireTransferBasics = ({
  children,
  redirectTo,
}: {
  children: React.ReactNode;
  redirectTo: string;
}) => {
  const form = modules.transfers.TransferForm.useForm();
  const sourceAccount = modules.transfers.useSelectedSourceAccount();
  const destinationAccount = modules.transfers.useSelectedDestinationAccount();

  if (!sourceAccount || !destinationAccount || form.values.amount <= 0) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

const providers = [modules.transfers.bootstrap.Provider];

const TransferRoutes = ({ limits }: { limits: LimitsForDisplayType }) => {
  const resetTransfer = modules.transfers.useResetTransfer();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    resetTransfer();
  }, []);

  const fromAccountParam = searchParams.get("from_account_id") as typeof initialValues.fromAccount | null;
  const toAccountParam = searchParams.get("to_account_id") as typeof initialValues.toAccount | null;
  const amountParam = parseInt(searchParams.get("amount") ?? "0", 10);

  const updatedInitialValues = {
    ...initialValues,
    // override the initial values in DBBL to "Once"
    // to be compatible with azul frequency selector in TransferDetails.tsx
    frequency: FREQUENCIES.ONCE,
    ...(fromAccountParam && { fromAccount: fromAccountParam }),
    ...(toAccountParam && { toAccount: toAccountParam }),
    ...(amountParam > 0 && {
      amount: cents(amountParam),
    }),
  };

  return (
    <ComposeProviders components={providers}>
      <modules.transfers.TransferForm.Provider
        initialValues={updatedInitialValues}
      >
        <Container>
          <Routes>
            <Route path="/*" element={<TransfersContainer limits={limits} />} />
            <Route
              path={`${TRANSFER_DETAILS_ROUTE}/*`}
              element={
                <RequireTransferBasics redirectTo={`/${TRANSFERS_ROUTE}`}>
                  <TransferDetailsComponent />
                </RequireTransferBasics>
              }
            />
            <Route
              path={TRANSFER_REVIEW_ROUTE}
              element={
                <RequireTransferBasics redirectTo={`/${TRANSFERS_ROUTE}`}>
                  <TransferReview />
                </RequireTransferBasics>
              }
            />
            <Route
              path={TRANSFER_RECEIPT_ROUTE}
              element={
                <RequireTransferBasics redirectTo={`/${TRANSFERS_ROUTE}`}>
                  <TransferReceipt />
                </RequireTransferBasics>
              }
            />
          </Routes>
        </Container>
      </modules.transfers.TransferForm.Provider>
    </ComposeProviders>
  );
};

export default TransferRoutes;
